import { FC, useState } from 'react';
import { TableListQueryResponse } from '../../../_core/interfaces/table-list-query.interface';
import { EngagementDto, PermissionDto } from '@families-link/shared';
import { useTranslation } from 'react-i18next';
import { useReactTable, getCoreRowModel, getFilteredRowModel, RowSelectionState } from '@tanstack/react-table';
import { CoreTable, CoreTableEmptyBody } from '../../../_core/components/_ui/core-table';
import {
	EngagementColumnName,
	useEngagementsTableColumns,
} from '../../hooks/table-columns/engagements-table-columns.hook';
import { AllowedEngagementActions } from './engagements-table-actions.component';

export interface EngagementsTableHandlers {
	onDelete?(engagement: EngagementDto): void;
	onEdit?(engagement: EngagementDto): void;
	onView?(e: React.MouseEvent<HTMLElement>, engagement: EngagementDto, isAdminRoute?: boolean): void;
	onSendEmail?(engagement: EngagementDto): void;
	onSendSMS?(engagement: EngagementDto): void;
	onArchive?(engagement: EngagementDto): void;
	onUnarchive?(engagement: EngagementDto): void;
	onSendReminder?(engagement: EngagementDto): void;
}
interface EngagementTableMeta {
	allFilteredEngagements: EngagementDto[];
}
type EngagementsTableProps = TableListQueryResponse<EngagementDto> &
	EngagementsTableHandlers & {
		engagementColumnsList: EngagementColumnName[];
		allowedEngagementActions: AllowedEngagementActions[];
		isSelectionMode?: boolean;
		isSorted?: boolean;
		meta?: EngagementTableMeta;
		isSkeletonLoader?: boolean;
		isLinguistLink?: boolean;
		isAdminRoute?: boolean;
	};

export const EngagementsTable: FC<EngagementsTableProps> = (props) => {
	const {
		data,
		sorting,
		onSortingChange,
		loading,
		hasMore,
		fetchMore,
		scrollRef,
		engagementColumnsList,
		allowedEngagementActions,
		isSelectionMode,
		isSorted,
		isSkeletonLoader,
		isLinguistLink,
		isAdminRoute,
		onDelete,
		onEdit,
		onView,
		onSendEmail,
		onSendSMS,
		onArchive,
		onUnarchive,
		rowSelection,
		onRowSelectionChange,
		onSendReminder,
		meta,
	} = props;

	const [t] = useTranslation();

	const columns = useEngagementsTableColumns(
		{
			onDelete,
			onEdit,
			onView,
			onSendEmail,
			onSendSMS,
			onArchive,
			onUnarchive,
			onSendReminder,
		},
		engagementColumnsList,
		allowedEngagementActions,
		isSelectionMode,
		isSorted,
		isSkeletonLoader,
		isLinguistLink,
		isAdminRoute
	);

	const table = useReactTable<EngagementDto>({
		data,
		columns,
		state: {
			sorting,
			rowSelection,
		},
		onSortingChange,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		enableRowSelection: true,
		onRowSelectionChange,
		getRowId: (row) => row._id,
		meta,
	});

	return (
		<CoreTable
			px={0}
			ref={scrollRef}
			loading={loading}
			hasMore={hasMore}
			threshold={200}
			emptyListComponent={<CoreTableEmptyBody description={t('engagements.no-engagements')} />}
			fetchMore={fetchMore}
			rowModel={table.getRowModel()}
			headerGroups={table.getHeaderGroups()}
		/>
	);
};
